<template>
  <van-popup
    v-model:show="visibleModel"
    round
    :close-on-click-overlay="false"
    class="summary-popup"
    @close="onClose"
  >
    <div class="summary">
      <!-- 图标(提交成功和查看待处理用同一图标) -->
      <div class="summary__check">
        <div class="summary__check-icon" :class="{ 'is-pending': mode === 'pending' }">
          {{ mode === 'pending' ? '⌛' : '✓' }}
        </div>
      </div>
      <div class="summary__title">
        {{ mode === 'pending' ? $t('recharge.pendingTitle') : $t('recharge.submittedTitle') }}
      </div>
      <div class="summary__hint">{{ $t('recharge.submittedHint') }}</div>

      <div class="summary__grid">
        <!-- 订单号 -->
        <div v-if="info.orderId" class="summary__row copyable">
          <div class="k">{{ $t('recharge.orderId') }}</div>
          <div class="v mono">#{{ info.orderId }}</div>
          <div class="btn" @click="onCopy(String(info.orderId))">{{ $t('common.copy') }}</div>
        </div>

        <!-- 转入方式(不复制) -->
        <div class="summary__row">
          <div class="k">{{ $t('recharge.method') }}</div>
          <div class="v">{{ info.methodName || '-' }}</div>
        </div>

        <!-- 收款人(不复制) -->
        <div v-if="info.accountName" class="summary__row">
          <div class="k">{{ $t('recharge.accountName') }}</div>
          <div class="v">{{ info.accountName }}</div>
        </div>

        <!-- 收款账号(复制) -->
        <div v-if="info.accountNo" class="summary__row copyable">
          <div class="k">{{ $t('recharge.accountNo') }}</div>
          <div class="v mono">{{ info.accountNo }}</div>
          <div class="btn" @click="onCopy(info.accountNo)">{{ $t('common.copy') }}</div>
        </div>

        <!-- 充值金额(复制) -->
        <div class="summary__row copyable">
          <div class="k">{{ $t('recharge.amount') }}</div>
          <div class="v mono">₡{{ info.amount }}</div>
          <div class="btn" @click="onCopy(String(info.amount))">{{ $t('common.copy') }}</div>
        </div>

        <!-- 用户手机号(复制,SINPE Descripción 用) -->
        <div v-if="info.mobile" class="summary__row copyable">
          <div class="k">{{ $t('recharge.yourMobile') }}</div>
          <div class="v mono">{{ info.mobile }}</div>
          <div class="btn" @click="onCopy(info.mobile)">{{ $t('common.copy') }}</div>
        </div>
      </div>

      <div v-if="info.isSinpe" class="summary__notice">
        ⚠️ {{ $t('recharge.sinpeReminder') }}
      </div>

      <div class="summary__actions">
        <van-button block round type="primary" class="brand-btn" @click="onConfirm">
          {{ $t('recharge.gotIt') }}
        </van-button>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue';
import { Toast } from 'vant';
import { useI18n } from 'vue-i18n';
import { copyText } from '@/utils';

const { t } = useI18n();

const props = defineProps({
  show: { type: Boolean, default: false },
  info: {
    type: Object,
    default: () => ({}),
    // shape:
    //   orderId:     订单 ID(top_up_fee.id)
    //   methodName:  方式名(SINPE Móvil)
    //   accountName: 收款人姓名
    //   accountNo:   收款账号
    //   amount:      金额
    //   mobile:      用户手机
    //   isSinpe:     是否 SINPE(触发底部橙色提醒)
  },
  mode: {
    type: String,
    default: 'submitted', // 'submitted' | 'pending'
  },
});
const emit = defineEmits(['update:show', 'confirm']);

const visibleModel = computed({
  get: () => props.show,
  set: (v) => emit('update:show', v),
});

const onClose = () => emit('update:show', false);
const onConfirm = () => emit('confirm');

const onCopy = async (text) => {
  const ok = await copyText(text);
  if (ok) Toast.success(t('common.copied'));
  else Toast.fail(t('common.copyFailed'));
};
</script>

<style lang="scss">
// 不用 scoped:popup 挂在 body 上
.summary-popup {
  width: 88vw !important;
  max-width: 720px !important;
  border-radius: 32px !important;
  overflow: hidden;
}
.summary {
  padding: 48px 40px 40px 40px;
  background: #fff;

  &__check {
    display: flex;
    justify-content: center;
    margin-bottom: 24px;
    &-icon {
      width: 120px;
      height: 120px;
      border-radius: 50%;
      background: linear-gradient(135deg, #10b981, #059669);
      color: #fff;
      font-size: 72px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
      &.is-pending {
        background: linear-gradient(135deg, #f59e0b, #d97706);
        box-shadow: 0 8px 24px rgba(245, 158, 11, 0.3);
        font-size: 56px;
      }
    }
  }
  &__title {
    text-align: center;
    font-size: 36px;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 12px;
  }
  &__hint {
    text-align: center;
    font-size: 24px;
    color: #6b7280;
    line-height: 1.5;
    margin-bottom: 32px;
  }
  &__grid {
    background: #f9fafb;
    border-radius: 20px;
    padding: 16px 24px;
    margin-bottom: 24px;
  }
  &__row {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 16px;
    padding: 20px 0;
    border-bottom: 1px dashed #e5e7eb;
    &:last-child { border-bottom: none; }
    &.copyable {
      grid-template-columns: auto 1fr auto;
    }

    .k {
      font-size: 24px;
      color: #6b7280;
      font-weight: 500;
    }
    .v {
      font-size: 30px;
      color: #1f2937;
      font-weight: 700;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: right;
    }
    .v.mono {
      font-family: 'Menlo', 'Consolas', 'Courier New', monospace;
      color: #764ba2;
      letter-spacing: 1px;
    }
    .btn {
      display: inline-block;
      padding: 12px 24px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: #fff;
      border-radius: 999px;
      font-size: 22px;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
      &:active { opacity: 0.85; transform: scale(0.98); }
    }
  }
  &__notice {
    background: linear-gradient(135deg, #fff8e1, #ffecb3);
    border-left: 6px solid #f59e0b;
    border-radius: 12px;
    padding: 20px 24px;
    font-size: 24px;
    color: #78350f;
    line-height: 1.5;
    margin-bottom: 24px;
  }
  &__actions .brand-btn {
    height: 96px !important;
    font-size: 32px !important;
  }
}
</style>
